import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  useEventCallback,
} from '@mui/material';
import {
  CancellationDueDeath,
  ChooseDate,
  DeathDataForm,
} from 'cancellations/view-models/cancellation-due-death';
import { memo } from 'react';
import ChooseDateComponent from 'shell/components/shared/choice-date';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import FormItalyEvent from '../../../../shell/components/shared/event-form/form-italy-event';
import FormForeignEventMemo from '../../../../shell/components/shared/event-form/form-foreign-event';
import DocumentActForm from 'shell/components/shared/act';
import { useSearchParams } from 'react-router-dom';
import { ItalianMunicipality } from 'shell/components/shared/event-form/form-italy-event';
import { ForeignMunicipality } from 'shell/components/shared/event-form/form-foreign-event';

interface DeathDataFormProps {
  deathDataForm?: DeathDataForm;
  errors: FormikErrors<DeathDataForm> | undefined;
  touched: FormikTouched<DeathDataForm> | undefined;
  handleFormFieldChange: (
    key: keyof DeathDataForm,
    value: DeathDataForm[keyof DeathDataForm]
  ) => void;
  handleFormFieldChanges: (values: DeathDataForm) => void;
  disabled?: boolean;
}

const DeathDataFormComponent = ({
  deathDataForm,
  errors,
  touched,
  handleFormFieldChange,
  handleFormFieldChanges,
  disabled,
}: DeathDataFormProps): React.ReactElement => {
  const formContext = useFormikContext<CancellationDueDeath>();
  const onChangeRadio = useEventCallback((value: string) => {
    if (deathDataForm?.happenInItaly) {
      handleFormFieldChanges({
        ...(deathDataForm || {}),
        eventItaly: undefined,
        happenInItaly: value === 'true',
      });
    } else {
      handleFormFieldChanges({
        ...(deathDataForm || {}),
        eventForeign: undefined,
        happenInItaly: value === 'true',
      });
    }
  });

  const [queryParams] = useSearchParams();

  const coniuge = JSON.parse(queryParams.get('coniuge') || 'false');

  return (
    <Grid container spacing={1}>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12}>
          <ChooseDateComponent
            label="Data evento"
            helperText={(errors?.eventDate as FormikErrors<ChooseDate>)?.date}
            selected={deathDataForm?.eventDate?.date}
            choice={deathDataForm?.eventDate?.type}
            onChange={(date, type) => {
              handleFormFieldChange('eventDate', { date, type });
            }}
            error={touched?.eventDate && Boolean(errors?.eventDate)}
            disabled={disabled}
          />
        </Grid>
        {formContext.values.showCheckboxDeathResident ? (
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  id="death-coniuge"
                  checked={deathDataForm?.deathPartnerResident || coniuge}
                  disabled={coniuge || disabled}
                  onChange={() =>
                    handleFormFieldChange(
                      'deathPartnerResident',
                      !deathDataForm?.deathPartnerResident
                    )
                  }
                  inputProps={{ 'aria-label': 'Morte coniugi residenti' }}
                />
              }
              label="Morte coniugi residenti"
            />
          </Grid>
        ) : undefined}
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Evento"
              id="happeInItaly"
              value={deathDataForm?.happenInItaly || 'false'}
              onChange={(event, value) => {
                onChangeRadio(value);
              }}
            >
              <FormControlLabel
                value="false"
                control={<Radio />}
                label="Evento Italia"
                disabled={disabled}
              />
              <FormControlLabel
                value="true"
                control={<Radio />}
                label="Evento Stato estero"
                disabled={disabled}
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} container spacing={0} columnSpacing={{ xs: 0, md: 1 }}>
          {!deathDataForm?.happenInItaly ? (
            <FormItalyEvent
              fromDate={deathDataForm?.eventDate?.date}
              form={deathDataForm?.eventItaly}
              handleChange={(eventItaly) => handleFormFieldChange('eventItaly', eventItaly)}
              errors={errors?.eventItaly as FormikErrors<ItalianMunicipality>}
              touched={touched?.eventItaly as unknown as FormikTouched<ItalianMunicipality>}
              disabled={disabled}
            />
          ) : (
            <FormForeignEventMemo
              form={deathDataForm?.eventForeign}
              handleChange={(eventForeign) => handleFormFieldChange('eventForeign', eventForeign)}
              errors={errors?.eventForeign as FormikErrors<ForeignMunicipality>}
              touched={touched?.eventForeign as unknown as FormikTouched<ForeignMunicipality>}
              disabled={disabled}
            />
          )}
        </Grid>
      </Grid>
      <DocumentActForm
        values={deathDataForm}
        onChange={(key, value) => {
          handleFormFieldChange(key, value);
        }}
        onChanges={(values) => {
          handleFormFieldChanges(values);
        }}
        errors={errors}
        touched={touched}
        disabled={disabled}
      />
    </Grid>
  );
};

DeathDataFormComponent.displayName = 'Death data accordion component';

const DeathDataFormComponentMemo = memo(DeathDataFormComponent);

export default DeathDataFormComponentMemo;
